import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MessageService } from "primeng/api";
import { TranslocoService } from "@ngneat/transloco";
import { markProjectFollowed } from '../social-feed/social-feed.component';
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AuthService } from "src/app/service/auth.service";
import { SocialService } from "src/app/service/social.service";
import { SocialPostResponse } from "src/app/model/social/social.types";

@Component({
  selector: "app-social-explore-detail",
  templateUrl: "./social-explore-detail.component.html",
  styleUrls: ["./social-explore-detail.component.css"],
})
export class SocialExploreDetailComponent implements OnInit, OnDestroy {
  mainPost: SocialPostResponse | null = null;
  morePosts: SocialPostResponse[] = [];
  moreCursor: string | null = null;
  loading = true;
  loadingMore = false;
  currentUserId?: string;
  currentUserAvatarUrl: string | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private social: SocialService,
    private auth: AuthService,
    private messageService: MessageService,
    private translocoService: TranslocoService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.currentUserId = this.auth.getUserInformations();
    if (this.currentUserId) {
      this.social.getProfile(this.currentUserId).pipe(takeUntil(this.destroy$)).subscribe({
        next: (p) => { this.currentUserAvatarUrl = p.avatarUrl ?? null; },
        error: () => {},
      });
    }
    this.route.paramMap.subscribe((pm) => {
      const id = pm.get("postId");
      if (!id) {
        this.router.navigate(["/feed"]);
        return;
      }
      this.loadPost(id);
    });
  }

  /** Seguiu por um card: o post principal e os demais do mesmo projeto escondem o botão também. */
  onProjectFollowedFromCard(projectId: string): void {
    const all = this.mainPost ? [this.mainPost, ...this.morePosts] : this.morePosts;
    markProjectFollowed(all, projectId);
  }

  private loadPost(postId: string): void {
    this.loading = true;
    this.social.getPost(postId).subscribe({
      next: (post) => {
        this.mainPost = post;
        this.loading = false;
        this.hydrateMissingProjectInfo(this.mainPost);
        this.loadMoreExplore(true);
      },
      error: (err) => {
        this.loading = false;
        this.messageService.add({
          severity: "error",
          summary: this.translocoService.translate('socialFeed.postLabel'),
          detail: SocialService.parseHttpErrorMessage(err),
        });
        this.router.navigate(["/feed"]);
      },
    });
  }

  /**
   * Workaround: `GET /posts/{id}` está retornando `projectName`/`projectAvatarUrl`
   * nulos mesmo quando o `projectId` existe (campos só são preenchidos nos
   * endpoints de listagem). Busca o projeto e patcheia o post in-place.
   */
  private hydrateMissingProjectInfo(post: SocialPostResponse | null): void {
    if (!post || !post.projectId || post.projectName) return;
    this.social.getProjectProfile(post.projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (project) => {
        if (this.mainPost && this.mainPost.id === post.id) {
          this.mainPost.projectName = project.projectName;
          this.mainPost.projectAvatarUrl = project.avatarUrl ?? null;
        }
      },
      error: () => {},
    });
  }

  loadMoreExplore(reset: boolean): void {
    if (reset) {
      this.morePosts = [];
      this.moreCursor = null;
    }
    this.loadingMore = true;
    this.social.getExplore(20, reset ? undefined : this.moreCursor).subscribe({
      next: (res) => {
        const filtered = res.items.filter((p) => p.id !== this.mainPost?.id);
        this.morePosts = reset ? filtered : [...this.morePosts, ...filtered];
        this.moreCursor = res.nextCursor ?? null;
        this.loadingMore = false;
      },
      error: () => {
        this.loadingMore = false;
      },
    });
  }

  loadMore(): void {
    if (!this.moreCursor || this.loadingMore) return;
    this.loadMoreExplore(false);
  }

  onPostChanged(): void {
    if (this.mainPost) {
      this.loadPost(this.mainPost.id);
    }
  }

  goBack(): void {
    this.router.navigate(["/feed"], { queryParams: { tab: "explore" } });
  }
}
